// Cesium
import {
  GeoJsonDataSource,
  ConstantProperty,
  HeightReference,
  HeadingPitchRange,
  Math as CesiumMath
} from "cesium/Build/Cesium/Cesium";

// Utils
import { getColorByString } from "utils/Cesium/CesiumUtils";
import { getDatasourceLayerByName } from "utils/Cesium/CesiumDatasourceUtils";
import { removeEntityByNameAndProperty } from "utils/Cesium/CesiumEntitiesUtils";

// Other
import { siteLayerName } from "assets/global";

/**
 * Adds a site to the Cesium viewer as a polygon entity
 *
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Object} site - The site object containing area geometry and metadata
 * @param {String} color - The color for the site polygon (rgba format with opacity)
 * @param {Number} scenarioId - The scenario ID for entity identification
 *
 * @returns {Promise<void>}
 */
export async function cesiumAddSite(viewer, site, color, scenarioId) {
  // Validate that the site has area geometry
  if (!site?.area || !site?.area?.coordinates) {
    console.warn(`Site ${site?.id} has no area geometry to display`);
    return;
  }

  try {
    // Get or create the sites datasource
    let sitesDataSource = getDatasourceLayerByName(viewer, siteLayerName);

    if (!sitesDataSource) {
      // Create the datasource if it doesn't exist
      sitesDataSource = new GeoJsonDataSource(siteLayerName);
      await viewer.dataSources.add(sitesDataSource);
    }

    // Parse the color (rgba format)
    const cesiumColor = getColorByString(color);

    // Create a GeoJSON feature from the site area
    const geoJsonFeature = {
      type: "Feature",
      geometry: site.area,
      properties: {
        id: site.id,
        name: site.name,
        rank: site.rank,
        scenario: site.scenario,
        scenarioId: scenarioId,
        siteId: site.id
      }
    };

    // Load the GeoJSON feature into a temporary datasource
    const tempDataSource = await GeoJsonDataSource.load(geoJsonFeature, {
      clampToGround: true
    });

    // Get the entity from the temporary datasource
    const entities = tempDataSource.entities.values;

    if (entities.length > 0) {
      const entity = entities[0];

      // Set entity name and custom properties for identification
      entity.name = `site_${scenarioId}_${site.id}`;
      entity.properties = new ConstantProperty({
        id: site.id,
        siteId: site.id,
        scenarioId: scenarioId,
        name: site.name,
        rank: site.rank
      });

      // Configure the polygon appearance
      if (entity.polygon) {
        entity.polygon.material = cesiumColor;
        entity.polygon.outline = true;
        entity.polygon.outlineColor = getColorByString(color, 1.0); // Full opacity for outline
        entity.polygon.outlineWidth = 2;

        // Clamp to ground to ensure visibility on terrain
        entity.polygon.heightReference = HeightReference.CLAMP_TO_GROUND;

        // Remove height and extrudedHeight to let it clamp properly
        entity.polygon.height = undefined;
        entity.polygon.extrudedHeight = undefined;
      }

      // Add the entity to the sites datasource
      sitesDataSource.entities.add(entity);
    }

    // Request render
    viewer.scene.requestRender();
  } catch (error) {
    console.error(`Error adding site ${site?.id} to Cesium:`, error);
  }
}

/**
 * Removes a solar site from the Cesium viewer
 *
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Number} siteId - The ID of the site to remove
 * @param {Number} scenarioId - The scenario ID for entity identification
 *
 * @returns {void}
 */
export function cesiumRemoveSite(viewer, siteId, scenarioId) {
  try {
    const sitesDataSource = getDatasourceLayerByName(viewer, siteLayerName);

    if (!sitesDataSource) {
      return;
    }

    // Remove entity by name
    const entityName = `site_${scenarioId}_${siteId}`;
    const entity = sitesDataSource.entities.getById(entityName);

    if (entity) {
      sitesDataSource.entities.remove(entity);
    } else {
      // Fallback: remove by property matching
      removeEntityByNameAndProperty(
        viewer,
        sitesDataSource.entities,
        entityName,
        "siteId",
        siteId
      );
    }

    viewer.scene.requestRender();
  } catch (error) {
    console.error(`Error removing site ${siteId} from Cesium:`, error);
  }
}

export function cesiumClearAllSites(viewer) {
  const sitesDataSource = getDatasourceLayerByName(viewer, siteLayerName);
  if (sitesDataSource) {
    sitesDataSource.entities.removeAll();
    viewer.scene.requestRender();
  }
}

/**
 * Updates the color of an existing site in the Cesium viewer
 *
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Number} siteId - The ID of the site to update
 * @param {Number} scenarioId - The scenario ID for entity identification
 * @param {String} color - The new color in rgba format (e.g., "rgba(18, 153, 236, 0.6)")
 *
 * @returns {void}
 */
export function cesiumSetSiteColor(viewer, siteId, scenarioId, color) {
  try {
    const sitesDataSource = getDatasourceLayerByName(viewer, siteLayerName);

    if (!sitesDataSource) {
      console.warn("Sites datasource not found");
      return;
    }

    const entityName = `site_${scenarioId}_${siteId}`;
    const entities = sitesDataSource.entities;

    // Suspend events while updating to batch changes
    entities.suspendEvents();

    // Find the entity
    const entity = entities.values.find(e => e.name === entityName);

    if (entity && entity.polygon) {
      // Parse the new color
      const cesiumColor = getColorByString(color);

      // Update the polygon material color
      // Try different methods based on the material structure
      if (entity.polygon.material?.color?.setValue) {
        // If it's a Property with setValue method
        entity.polygon.material.color.setValue(cesiumColor);
      } else if (entity.polygon.material?.color) {
        // If it has a color property but no setValue
        entity.polygon.material.color = cesiumColor;
      } else {
        // Fallback: set the material directly
        entity.polygon.material = cesiumColor;
      }

      // Update outline color
      if (entity.polygon.outlineColor?.setValue) {
        entity.polygon.outlineColor.setValue(getColorByString(color, 1.0));
      } else {
        entity.polygon.outlineColor = getColorByString(color, 1.0);
      }
    } else {
      console.warn(`Site entity ${entityName} not found for color update`);
    }

    // Resume events and request render
    entities.resumeEvents();
    viewer.scene.requestRender();
  } catch (error) {
    console.error(`Error updating site ${siteId} color in Cesium:`, error);
  }
}

/**
 * Zooms the Cesium viewer to fit all the provided sites' extents
 *
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Array} sites - Array of site objects containing area geometries
 * @param {Object} options - Optional configuration for the zoom animation
 * @param {Number} options.duration - Animation duration in seconds (default: 2)
 * @param {Number} options.range - Distance from center in meters (default: 500)
 * @param {Number} options.pitch - Camera pitch in degrees (default: -45, looking down at an angle)
 *
 * @returns {Promise<Boolean>} - Returns true if zoom was successful, false otherwise
 */
export async function cesiumZoomToSitesExtent(viewer, sites, options = {}) {
  const { duration = 2, range = 500, pitch = -75 } = options;

  // Validate input
  if (!viewer || !sites || sites.length === 0) {
    console.warn(
      "Invalid viewer or sites array provided to cesiumZoomToSitesExtent"
    );
    return false;
  }

  try {
    const sitesDataSource = getDatasourceLayerByName(viewer, siteLayerName);

    if (!sitesDataSource) {
      console.warn("Sites datasource not found for zoom");
      return false;
    }

    if (sitesDataSource.entities.values.length === 0) {
      console.warn("No site entities found for zoom");
      return false;
    }

    // Ensure the datasource is visible
    sitesDataSource.show = true;

    // Create a set of site IDs we want to zoom to for quick lookup
    const targetSiteIds = new Set(sites.map(site => site.id));

    // Filter entities to only include the target sites
    const targetEntities = sitesDataSource.entities.values.filter(entity => {
      const siteId = entity.properties?.getValue(
        viewer.clock.currentTime
      )?.siteId;
      return siteId && targetSiteIds.has(siteId);
    });

    if (targetEntities.length === 0) {
      console.warn("No matching site entities found for zoom");
      return false;
    }

    // Fly to only the target entities with a fixed closer range
    await viewer.flyTo(targetEntities, {
      duration: duration,
      offset: new HeadingPitchRange(
        CesiumMath.toRadians(0), // heading: 0 degrees (north)
        CesiumMath.toRadians(pitch), // pitch: looking down at an angle
        range // range: distance from center in meters
      )
    });

    viewer.scene.requestRender();
    return true;
  } catch (error) {
    console.error("Error zooming to sites extent in Cesium:", error);
    return false;
  }
}
